import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
// The resizable image node lives in its own module since 2026-09-02, shared
// with the TME Wiki's editor, which had been built on TipTap's plain image.
import ResizableImage from './ResizableImage';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import CodeBlock from '@tiptap/extension-code-block';
import Blockquote from '@tiptap/extension-blockquote';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import {
    FaBold,
    FaItalic,
    FaUnderline,
    FaStrikethrough,
    FaListUl,
    FaListOl,
    FaQuoteRight,
    FaMinus,
    FaCode,
    FaLink,
    FaUnlink,
    FaImage,
    FaTable,
    FaFont,
    FaHighlighter,
    FaUndo,
    FaRedo,
    FaTrash,
    FaArrowUp,
    FaArrowDown,
    FaArrowLeft,
    FaArrowRight,
    FaGripLines,
    FaGripLinesVertical,
    FaTh,
    FaEquals
} from 'react-icons/fa';

const WikiPageEditor = ({
    content,
    title,
    onChange,
    onImageUpload,
    readOnly = false,
    placeholder = 'Start writing...',
    debounceMs = 1500
}) => {
    const debounceTimer = useRef(null);
    const fileInputRef = useRef(null);
    const onImageUploadRef = useRef(onImageUpload);
    const editorRef = useRef(null);
    const [showTablePicker, setShowTablePicker] = useState(false);
    const [tablePickerSize, setTablePickerSize] = useState({ rows: 0, cols: 0 });
    const tablePickerRef = useRef(null);
    const tableToolbarRef = useRef(null);
    const [isInTable, setIsInTable] = useState(false);
    const tableActionInProgressRef = useRef(false);

    // Keep the ref updated with the latest callback
    useEffect(() => {
        onImageUploadRef.current = onImageUpload;
    }, [onImageUpload]);

    // Helper function to upload image and insert into editor
    const uploadAndInsertImage = useCallback(async (file) => {
        console.log('uploadAndInsertImage called with file:', file?.name, file?.type);
        console.log('onImageUploadRef.current:', !!onImageUploadRef.current);
        console.log('editorRef.current:', !!editorRef.current);

        if (!onImageUploadRef.current) {
            console.error('uploadAndInsertImage: onImageUploadRef.current is null/undefined');
            return false;
        }
        if (!editorRef.current) {
            console.error('uploadAndInsertImage: editorRef.current is null/undefined');
            return false;
        }

        try {
            console.log('Calling onImageUpload callback...');
            const imageUrl = await onImageUploadRef.current(file);
            console.log('Image upload result:', imageUrl);
            if (imageUrl && editorRef.current) {
                console.log('Inserting image into editor...');
                editorRef.current.chain().focus().setImage({ src: imageUrl }).run();
                console.log('Image inserted successfully');
                return true;
            } else {
                console.error('Image upload returned null/undefined or editor ref is gone');
            }
        } catch (error) {
            console.error('Failed to upload image:', error);
        }
        return false;
    }, []);

    const editor = useEditor({
        // TipTap injects its base CSS as a <style> element at runtime, which a
        // Content-Security-Policy of style-src 'self' blocks. That stylesheet now
        // lives in index.css instead (search "TipTap base styles"). Keep the two
        // in step: if TipTap is upgraded, re-check its style.ts against ours.
        //
        // injectNonce is not an option here - nonces must be generated per
        // response, and a static S3 + CloudFront site cannot do that.
        injectCSS: false,
        // ⛔ WITHOUT THIS THE TOOLBAR NEVER UPDATES AS THE CARET MOVES (ISS-057).
        // @tiptap/react@3 re-renders on a transaction ONLY when this is true -
        // it defaulted the other way in v2, which is what every `isActive`
        // toolbar in this file was written against. Same fix as
        // tmeWiki/TmeWikiPageEditor.js (TWM-06); those two are the only
        // useEditor call sites in the repository.
        shouldRerenderOnTransaction: true,
        extensions: [
            StarterKit.configure({
                codeBlock: false,
                blockquote: false,
                horizontalRule: false
            }),
            Underline,
            ResizableImage.configure({
                inline: false,
                allowBase64: false
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-primary-600 underline hover:text-primary-800'
                }
            }),
            Table.configure({
                resizable: true,
                handleWidth: 5,
                cellMinWidth: 80,
                lastColumnResizable: true,
                allowTableNodeSelection: true
            }),
            TableRow,
            TableCell.configure({
                HTMLAttributes: {}
            }),
            TableHeader.configure({
                HTMLAttributes: {}
            }),
            CodeBlock.configure({
                HTMLAttributes: {
                    class: 'bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto'
                }
            }),
            Blockquote.configure({
                HTMLAttributes: {
                    class: 'border-l-4 border-gray-300 pl-4 italic text-gray-600'
                }
            }),
            HorizontalRule,
            TextStyle,
            Color,
            Highlight.configure({
                multicolor: true
            }),
            Placeholder.configure({
                placeholder
            })
        ],
        content: content,
        editable: !readOnly,
        editorProps: {
            handleDOMEvents: {
                paste: (view, event) => {
                    console.log('handleDOMEvents.paste triggered');
                    const items = event.clipboardData?.items;
                    if (!items) {
                        console.log('No clipboard items');
                        return false;
                    }

                    // Check if there's an image in the clipboard
                    const imageItem = Array.from(items).find(item => item.type.startsWith('image/'));
                    if (!imageItem) {
                        console.log('No image in clipboard');
                        return false;
                    }

                    // Check if we have upload handler
                    if (!onImageUploadRef.current) {
                        console.log('No onImageUpload callback');
                        return false;
                    }

                    console.log('Found image in clipboard:', imageItem.type);
                    event.preventDefault();
                    event.stopPropagation();

                    const file = imageItem.getAsFile();
                    if (file) {
                        console.log('Got file from clipboard:', file.name, file.type, file.size);
                        uploadAndInsertImage(file);
                    }
                    return true;
                },
                drop: (view, event) => {
                    console.log('handleDOMEvents.drop triggered');
                    const files = event.dataTransfer?.files;
                    if (!files || files.length === 0) {
                        console.log('No files in drop');
                        return false;
                    }

                    // Check if we have upload handler
                    if (!onImageUploadRef.current) {
                        console.log('No onImageUpload callback');
                        return false;
                    }

                    // Find first image file
                    const imageFile = Array.from(files).find(file => file.type.startsWith('image/'));
                    if (!imageFile) {
                        console.log('No image in dropped files');
                        return false;
                    }

                    console.log('Found image in drop:', imageFile.name, imageFile.type, imageFile.size);
                    event.preventDefault();
                    event.stopPropagation();

                    uploadAndInsertImage(imageFile);
                    return true;
                }
            }
        },
        onUpdate: ({ editor }) => {
            if (onChange) {
                if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                }
                debounceTimer.current = setTimeout(() => {
                    onChange(editor.getHTML());
                }, debounceMs);
            }
        },
        onSelectionUpdate: ({ editor }) => {
            setIsInTable(editor.isActive('table'));
        },
        onFocus: ({ editor }) => {
            setIsInTable(editor.isActive('table'));
        },
        onBlur: () => {
            // If a table action is in progress, don't hide the toolbar
            // The action will refocus the editor when complete
            if (tableActionInProgressRef.current) {
                return;
            }
            setIsInTable(false);
        }
    });

    // Store editor reference for use in handlers
    useEffect(() => {
        editorRef.current = editor;
    }, [editor]);

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content, { emitUpdate: false });
        }
    }, [content, editor]);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const handleImageUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(async (event) => {
        console.log('handleFileChange called');
        const file = event.target.files?.[0];
        console.log('Selected file:', file?.name, file?.type);

        if (!file) {
            console.log('No file selected');
            return;
        }
        if (!onImageUpload) {
            console.error('onImageUpload prop is not provided');
            return;
        }

        try {
            console.log('Calling onImageUpload callback...');
            const imageUrl = await onImageUpload(file);
            console.log('Image upload result:', imageUrl);
            if (imageUrl && editor) {
                console.log('Inserting image into editor...');
                editor.chain().focus().setImage({ src: imageUrl }).run();
                console.log('Image inserted successfully');
            } else {
                console.error('Image upload returned null/undefined or editor is null');
            }
        } catch (error) {
            console.error('Failed to upload image:', error);
        }
        event.target.value = '';
    }, [editor, onImageUpload]);

    const setLink = useCallback(() => {
        if (!editor) return;

        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Enter URL:', previousUrl);

        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    const insertTable = useCallback((rows = 3, cols = 3) => {
        if (!editor) return;
        // Insert table - the CSS already handles width: 100% and table-layout: fixed
        editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
        setShowTablePicker(false);
    }, [editor]);

    // Distribute columns equally by clearing colwidth on all cells
    const distributeColumnsEqually = useCallback(() => {
        if (!editor) return;

        tableActionInProgressRef.current = true;

        try {
            // Get current state to find cells with colwidth
            const { state } = editor;
            const $pos = state.selection.$from;

            // Find the table containing the current selection
            let tableStart = null;
            let tableNode = null;

            for (let depth = $pos.depth; depth > 0; depth--) {
                const node = $pos.node(depth);
                if (node.type.name === 'table') {
                    tableNode = node;
                    tableStart = $pos.before(depth);
                    break;
                }
            }

            if (!tableNode || tableStart === null) {
                editor.commands.focus();
                return;
            }

            // Use the editor's transaction system to clear colwidth on all cells
            editor.chain().focus().command(({ tr, state: cmdState }) => {
                cmdState.doc.nodesBetween(tableStart, tableStart + tableNode.nodeSize, (node, pos) => {
                    if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
                        if (node.attrs.colwidth) {
                            tr.setNodeMarkup(pos, null, { ...node.attrs, colwidth: null });
                        }
                    }
                });
                // Always return true to ensure focus is maintained
                return true;
            }).run();
        } finally {
            // Clear the flag after a short delay to allow blur event to be ignored
            setTimeout(() => {
                tableActionInProgressRef.current = false;
                // Re-check if we're still in a table and update state
                if (editor && editor.isActive('table')) {
                    setIsInTable(true);
                }
            }, 50);
        }
    }, [editor]);

    // Close table picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tablePickerRef.current && !tablePickerRef.current.contains(event.target)) {
                setShowTablePicker(false);
            }
        };
        if (showTablePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showTablePicker]);

    const setTextColour = useCallback(() => {
        if (!editor) return;
        const colour = window.prompt('Enter colour (e.g., #ff0000, red):', '#000000');
        if (colour) {
            editor.chain().focus().setColor(colour).run();
        }
    }, [editor]);

    const setHighlightColour = useCallback(() => {
        if (!editor) return;
        const colour = window.prompt('Enter highlight colour (e.g., #ffff00, yellow):', '#ffff00');
        if (colour) {
            editor.chain().focus().toggleHighlight({ color: colour }).run();
        }
    }, [editor]);

    if (!editor) {
        return (
            <div className="border border-gray-300 rounded-lg p-4 min-h-64 bg-gray-50 flex items-center justify-center">
                <span className="text-gray-400">Loading editor...</span>
            </div>
        );
    }

    const ToolbarButton = ({ onClick, isActive, disabled, title, children }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={`
                p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed
                ${isActive ? 'bg-gray-200 text-primary-600' : 'text-gray-600'}
            `}
        >
            {children}
        </button>
    );

    const ToolbarDivider = () => (
        <div className="w-px h-6 bg-gray-300 mx-1" />
    );

    // Table size picker grid component
    const TableSizePicker = () => {
        const maxRows = 8;
        const maxCols = 8;

        return (
            <div
                ref={tablePickerRef}
                className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50"
            >
                <div className="text-xs text-gray-500 mb-2 text-center">
                    {tablePickerSize.rows > 0 && tablePickerSize.cols > 0
                        ? `${tablePickerSize.rows} x ${tablePickerSize.cols}`
                        : 'Select size'}
                </div>
                <div
                    className="grid gap-0.5"
                    style={{ gridTemplateColumns: `repeat(${maxCols}, 1fr)` }}
                    onMouseLeave={() => setTablePickerSize({ rows: 0, cols: 0 })}
                >
                    {Array.from({ length: maxRows * maxCols }).map((_, index) => {
                        const row = Math.floor(index / maxCols) + 1;
                        const col = (index % maxCols) + 1;
                        const isHighlighted = row <= tablePickerSize.rows && col <= tablePickerSize.cols;

                        return (
                            <button
                                key={index}
                                type="button"
                                className={`w-4 h-4 border ${isHighlighted ? 'bg-primary-500 border-primary-600' : 'bg-gray-100 border-gray-300'} hover:bg-primary-300`}
                                onMouseEnter={() => setTablePickerSize({ rows: row, cols: col })}
                                onClick={() => insertTable(row, col)}
                            />
                        );
                    })}
                </div>
            </div>
        );
    };

    // Contextual table toolbar that shows when cursor is in a table
    const TableToolbar = () => {
        if (!isInTable) return null;

        // Button component for table toolbar with action tracking
        const TableButton = ({ onClick, disabled, title, children, className = '' }) => {
            const handleClick = (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (disabled) return;

                // Mark that an action is in progress to prevent blur from hiding toolbar
                tableActionInProgressRef.current = true;

                try {
                    if (onClick) {
                        onClick();
                    }
                } finally {
                    // Clear the flag after a short delay to allow blur event to be ignored
                    setTimeout(() => {
                        tableActionInProgressRef.current = false;
                        // Re-check if we're still in a table and update state
                        if (editor && editor.isActive('table')) {
                            setIsInTable(true);
                        }
                    }, 50);
                }
            };

            return (
                <button
                    type="button"
                    onClick={handleClick}
                    onMouseDown={(e) => {
                        // Prevent mousedown from causing editor blur
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    disabled={disabled}
                    title={title}
                    className={`
                        p-2 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600
                        ${className}
                    `}
                >
                    {children}
                </button>
            );
        };

        return (
            <div
                ref={tableToolbarRef}
                className="bg-blue-50 border-b border-blue-200 px-2 py-1.5 flex items-center flex-wrap gap-0.5"
                onMouseDown={(e) => {
                    // Prevent the mousedown from causing editor blur
                    e.preventDefault();
                }}
            >
                <span className="text-xs text-blue-700 font-medium mr-2">Table:</span>

                {/* Row operations */}
                <TableButton
                    onClick={() => editor.chain().focus().addRowBefore().run()}
                    title="Add row above"
                >
                    <div className="flex items-center">
                        <FaArrowUp className="w-3 h-3" />
                        <FaGripLines className="w-3 h-3 ml-0.5" />
                    </div>
                </TableButton>
                <TableButton
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                    title="Add row below"
                >
                    <div className="flex items-center">
                        <FaArrowDown className="w-3 h-3" />
                        <FaGripLines className="w-3 h-3 ml-0.5" />
                    </div>
                </TableButton>
                <TableButton
                    onClick={() => editor.chain().focus().deleteRow().run()}
                    title="Delete row"
                >
                    <div className="flex items-center text-red-600">
                        <FaTrash className="w-3 h-3" />
                        <FaGripLines className="w-3 h-3 ml-0.5" />
                    </div>
                </TableButton>

                <ToolbarDivider />

                {/* Column operations */}
                <TableButton
                    onClick={() => editor.chain().focus().addColumnBefore().run()}
                    title="Add column left"
                >
                    <div className="flex items-center">
                        <FaArrowLeft className="w-3 h-3" />
                        <FaGripLinesVertical className="w-3 h-3 ml-0.5" />
                    </div>
                </TableButton>
                <TableButton
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                    title="Add column right"
                >
                    <div className="flex items-center">
                        <FaArrowRight className="w-3 h-3" />
                        <FaGripLinesVertical className="w-3 h-3 ml-0.5" />
                    </div>
                </TableButton>
                <TableButton
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                    title="Delete column"
                >
                    <div className="flex items-center text-red-600">
                        <FaTrash className="w-3 h-3" />
                        <FaGripLinesVertical className="w-3 h-3 ml-0.5" />
                    </div>
                </TableButton>

                <ToolbarDivider />

                {/* Header toggle */}
                <TableButton
                    onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                    title="Toggle header row"
                >
                    <FaTh className="w-4 h-4" />
                </TableButton>

                {/* Equal column width */}
                <TableButton
                    onClick={distributeColumnsEqually}
                    title="Distribute columns equally"
                >
                    <FaEquals className="w-4 h-4" />
                </TableButton>

                <ToolbarDivider />

                {/* Delete table */}
                <TableButton
                    onClick={() => editor.chain().focus().deleteTable().run()}
                    title="Delete table"
                >
                    <div className="flex items-center text-red-600">
                        <FaTrash className="w-3 h-3" />
                        <FaTable className="w-3 h-3 ml-0.5" />
                    </div>
                </TableButton>
            </div>
        );
    };

    return (
        <div className="border border-gray-300 rounded-lg overflow-hidden">
            {!readOnly && (
                <div className="bg-gray-50 border-b border-gray-300 p-2 flex items-center flex-wrap gap-0.5">
                    {/* Text Formatting */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        isActive={editor.isActive('bold')}
                        title="Bold"
                    >
                        <FaBold className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        isActive={editor.isActive('italic')}
                        title="Italic"
                    >
                        <FaItalic className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        isActive={editor.isActive('underline')}
                        title="Underline"
                    >
                        <FaUnderline className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        isActive={editor.isActive('strike')}
                        title="Strikethrough"
                    >
                        <FaStrikethrough className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Headings */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        isActive={editor.isActive('heading', { level: 1 })}
                        title="Heading 1"
                    >
                        <span className="text-xs font-bold">H1</span>
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        isActive={editor.isActive('heading', { level: 2 })}
                        title="Heading 2"
                    >
                        <span className="text-xs font-bold">H2</span>
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        isActive={editor.isActive('heading', { level: 3 })}
                        title="Heading 3"
                    >
                        <span className="text-xs font-bold">H3</span>
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Lists */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        isActive={editor.isActive('bulletList')}
                        title="Bullet List"
                    >
                        <FaListUl className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        isActive={editor.isActive('orderedList')}
                        title="Numbered List"
                    >
                        <FaListOl className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        isActive={editor.isActive('blockquote')}
                        title="Quote"
                    >
                        <FaQuoteRight className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Blocks */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                        title="Horizontal Rule"
                    >
                        <FaMinus className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        isActive={editor.isActive('codeBlock')}
                        title="Code Block"
                    >
                        <FaCode className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Media */}
                    <ToolbarButton
                        onClick={setLink}
                        isActive={editor.isActive('link')}
                        title="Link"
                    >
                        <FaLink className="w-4 h-4" />
                    </ToolbarButton>
                    {editor.isActive('link') && (
                        <ToolbarButton
                            onClick={() => editor.chain().focus().unsetLink().run()}
                            title="Remove Link"
                        >
                            <FaUnlink className="w-4 h-4" />
                        </ToolbarButton>
                    )}
                    <ToolbarButton
                        onClick={handleImageUploadClick}
                        title="Insert Image"
                        disabled={!onImageUpload}
                    >
                        <FaImage className="w-4 h-4" />
                    </ToolbarButton>
                    <div className="relative">
                        <ToolbarButton
                            onClick={() => setShowTablePicker(!showTablePicker)}
                            isActive={showTablePicker}
                            title="Insert Table"
                        >
                            <FaTable className="w-4 h-4" />
                        </ToolbarButton>
                        {showTablePicker && <TableSizePicker />}
                    </div>

                    <ToolbarDivider />

                    {/* Colours */}
                    <ToolbarButton
                        onClick={setTextColour}
                        title="Text Colour"
                    >
                        <FaFont className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={setHighlightColour}
                        isActive={editor.isActive('highlight')}
                        title="Highlight"
                    >
                        <FaHighlighter className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {/* Undo/Redo */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        title="Undo"
                    >
                        <FaUndo className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        title="Redo"
                    >
                        <FaRedo className="w-4 h-4" />
                    </ToolbarButton>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        className="hidden"
                        onChange={handleFileChange}
                    />
                </div>
            )}

            {!readOnly && <TableToolbar />}

            <div className="p-4">
                <div className="wiki-doc-card min-h-64">
                    {title && <h1 className="wiki-doc-title">{title}</h1>}
                    <div className="wiki-doc focus:outline-none">
                        <EditorContent editor={editor} />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default WikiPageEditor;
